import React, { useEffect, useState } from 'react';
import AddEditCardForm from 'features/cards/components/add-edit-card-form/add-edit-card-form';
import AddEditCardImage from '../../components/add-edit-card-image/add-edit-card-image';

import './card-form-modal.scss';
import { useModal, ButtonVariant, ButtonType, CardIcon } from '@ampol/design-system';
import { ButtonColor } from '@ampol/design-system/lib';
import { useSelector, useDispatch } from 'react-redux';
import { getPlasticType, resetPlasticType } from 'actions/maintenanceActions';
import { switchExpiryDate } from 'utilities/card-utilities';
import Label from 'constants/labels';
import isEmpty from 'utilities/isEmpty';
import Env from 'Env';

interface CardFormModalProps {
  cardNumber?: string;
  onClose: () => void;
  onEvReissueComplete?: () => void;
  formId: string;
  onCardStatusChange?: (changes: any) => void;
}

/**
 * Describe component here
 */
const CardFormModal = ({
  cardNumber,
  onClose,
  onEvReissueComplete,
  formId,
  onCardStatusChange,
}: CardFormModalProps) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;
  const dispatch = useDispatch();
  const [modifiedCardDetails, setModifiedCardDetails] = useState<Record<string, any>>({});

  const cardDetail = useSelector((state: any) => state.cardDetailView.cardDetail);
  const plasticType = useSelector((state: any) => state.maintenanceStore.plasticType);
  const plasticTypeLoaded = useSelector((state: any) => state.maintenanceStore.plasticTypeLoaded);
  const accountDefaultsApiError = useSelector((state: any) => state.accountDefaults.isError);
  const createCardLoading = useSelector((state: any) => state.cardsStore.createCardLoading);
  const editCardLoading = useSelector((state: any) => state.cardDetailView.editCardLoading);

  useEffect(() => {
    if (!isEmpty(cardDetail) && cardDetail.plasticTypeCode) {
      dispatch(getPlasticType(cardDetail.plasticTypeCode));
    }

    return () => {
      dispatch(resetPlasticType());
    };
  }, [cardDetail]);

  const setIsFormValidfn = (isValid: boolean) => {
    if (isValid !== isFormValid) setIsFormValid(isValid);
  };

  const getCardDetails = (cardDetails: Record<string, any>) => {
    return {
      embossingLine2: cardDetails?.embossedCompanyName ?? '',
      embossingName: cardDetails?.embossedLastName ?? '',
      regoNumber: cardDetails?.registrationNumber ?? '',
      expiryDate: cardDetails?.expiryDate
        ? cardDetails?.expiryDate?.length === 4
          ? switchExpiryDate(cardDetails?.expiryDate)
          : cardDetails?.expiryDate
        : '',
    };
  };

  const formModal = useModal({
    id: 'card-form-details',
    title: cardNumber ? Label.EDIT_CARD : 'Add new card',
    subtitle: cardNumber ? cardNumber : '',
    content: (
      <AddEditCardForm
        onClose={onClose}
        onEvReissueComplete={isEvWay4Enabled ? onEvReissueComplete : undefined}
        cardDetails={cardNumber && cardDetail ? cardDetail : undefined}
        formId={formId}
        setIsFormValid={setIsFormValidfn}
        onCardChanges={onCardStatusChange}
        setModifiedCardDetails={setModifiedCardDetails}
      />
    ),
    variant: 'triple',
    color: ButtonColor.Blue,
    actions: [
      {
        label: 'Submit',
        variant: ButtonVariant.Primary,
        ...(!accountDefaultsApiError && { type: ButtonType.Submit }),
        inactive: createCardLoading || editCardLoading || accountDefaultsApiError,
      },
      {
        label: 'Cancel',
        variant: ButtonVariant.Secondary,
        onClick: () => onClose?.(),
        inactive: createCardLoading || editCardLoading,
      },
    ],
    formId: formId,
    onClose: onClose,
    icon:
      plasticTypeLoaded && plasticType?.image?.url ? (
        <CardIcon
          className='card-form-modal--icon'
          url={plasticType?.image?.url}
          onClick={() => cardImageModal.open()}
        />
      ) : (
        <></>
      ),
  });

  useEffect(() => {
    formModal.open();
  }, [cardNumber]);

  const cardImageModal = useModal({
    id: 'card-image-details',
    title: cardNumber ? Label.EDIT_CARD : 'Add new card',
    subtitle: cardNumber ? cardNumber : '',
    variant: 'triple',
    color: ButtonColor.Blue,
    content: (
      <AddEditCardImage
        plasticType={plasticType}
        {...getCardDetails({ ...(cardNumber && cardDetail), ...modifiedCardDetails })}
      />
    ),
    actions: [
      {
        label: 'Back',
        onClick: () => cardImageModal.close(),
      },
    ],
  });

  return <></>;
};

export default CardFormModal;
